/** Tiny data-fetching hook: loading, error code and manual reload. */

import { useCallback, useEffect, useState } from 'react';

import { ApiError } from '../api/client';

interface State<T> {
  data: T | null;
  loading: boolean;
  errorCode: string | null;
  errorParams: Record<string, unknown>;
  reload: () => void;
}

export function useApi<T>(loader: () => Promise<T>, deps: unknown[]): State<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [errorParams, setErrorParams] = useState<Record<string, unknown>>({});
  const [tick, setTick] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const run = useCallback(loader, deps);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    run()
      .then((result) => {
        if (cancelled) return;
        setData(result);
        setErrorCode(null);
        setErrorParams({});
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        if (err instanceof ApiError) {
          setErrorCode(err.code);
          setErrorParams(err.params);
        } else {
          setErrorCode('server.error');
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [run, tick]);

  return {
    data,
    loading,
    errorCode,
    errorParams,
    reload: () => setTick((value) => value + 1),
  };
}

/** Re-run a loader on an interval; used for live views. */
export function useInterval(callback: () => void, delayMs: number | null): void {
  useEffect(() => {
    if (delayMs === null) return;
    const handle = window.setInterval(callback, delayMs);
    return () => window.clearInterval(handle);
  }, [callback, delayMs]);
}
